import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Icons from '@shared/components/core/images/Icons';
import { borderRadius, gradientsFull } from '@shared/constants/Styles';
import { ButtonSize, ICoreButtonProps } from '@shared/models/components/ICoreButtonProps';
import { forwardRef } from 'react';
import { NavLink } from 'react-router';
import { MixinFocusVisible } from '../structure/DefaultStyles';
import { TextSize100, TextSize90 } from '../text/Typography';
import {
    aaaSizeStyles,
    buttonBaseStyles,
    disabledButtonStyles,
    disabledTextButtonStyles,
    sizeStyles,
} from './CommonButtonStyles';

// ─────────────────────────────────────────────
// VARIANT STYLES
// Each variant is a self-contained style block.
// They reference theme tokens only — no raw values.
// The base structure (size, spacing, touch target)
// comes from buttonBaseStyles and sizeStyles.
// ─────────────────────────────────────────────

const variantStyles = {
    primary: ({ theme }: { theme: any }) => css`
        font-weight: 700;
        background: ${theme.button.primary.background};
        ${theme.button.primary.backgroundImage && `background-image: ${gradientsFull.primary_button}`};
        border: 1px solid ${theme.button.primary.border};
        color: ${theme.button.primary.text};
        text-decoration: none;
        &:hover {
            color: ${theme.button.primary.hoverText};
            background: ${theme.button.primary.hoverBackground};
            svg {
                color: ${theme.button.primary.icon};
            }
        }
    `,
    secondary: ({ theme }: { theme: any }) => css`
        font-weight: 700;
        background: ${theme.button.secondary.background};
        border: 1px solid ${theme.button.secondary.border};
        color: ${theme.button.secondary.text};
        text-decoration: none;
        &:hover {
            color: ${theme.button.secondary.hoverText};
            background: ${theme.button.secondary.hoverBackground};
            svg {
                color: ${theme.button.secondary.icon};
            }
        }
    `,
    accent: ({ theme }: { theme: any }) => css`
        font-weight: 300;
        background: ${theme.button.accent.background};
        border: 1px solid transparent;
        border-radius: ${borderRadius.lg};
        color: ${theme.button.accent.text};
        text-decoration: none;
        &:hover {
            border-color: ${theme.button.accent.border};
            background: ${theme.button.accent.hoverBackground};
            color: ${theme.button.accent.hoverText};
            svg {
                color: ${theme.button.accent.icon};
            }
        }
    `,
    simple: ({ theme }: { theme: any }) => css`
        background: transparent;
        font-weight: 400;
        color: ${theme.button.simple.text};
        border: none;
        text-decoration: underline;
        text-decoration-color: ${theme.button.simple.text};
        min-width: fit-content;
        &:hover {
            box-shadow: 0 0 0 1px ${theme.button.simple.text};
            filter: none;
        }
    `,
    quick: ({ theme }: { theme: any }) => css`
        font-weight: 400;
        font-family: 'Archivo', sans-serif;
        background: ${theme.button.quick.background};
        border: 1px solid ${theme.button.quick.border};
        border-radius: ${borderRadius.lg};
        color: ${theme.button.quick.text};
        text-decoration: none;
        svg {
            color: ${theme.button.quick.border};
        }
        &:hover {
            background: ${theme.button.quick.hoverBackground};
            color: ${theme.button.quick.hoverText};
            text-decoration: underline;
            text-decoration-color: ${theme.button.quick.border};
            svg {
                color: ${theme.button.quick.icon};
            }
        }
    `,
    solidAccent: ({ theme }: { theme: any }) => css`
        font-weight: 300;
        background: ${theme.button.accent.background};
        border: 1px solid ${theme.color.accent};
        border-radius: ${borderRadius.lg};
        color: ${theme.color.accent};
        min-width: fit-content;
        text-decoration: none;
        &:hover {
            border-color: ${theme.button.accent.border};
            background: ${theme.button.accent.hoverBackground};
            color: ${theme.button.accent.border};
            svg {
                color: ${theme.button.accent.border};
            }
        }
    `,
    // ─────────────────────────────────────────────
    // ROUND SHELL/PANEL CONTROL VARIANTS
    // Shared color recipe behind FooterButton/SendButton
    // (card surface + accent icon, and accent-filled send).
    // ─────────────────────────────────────────────
    footer: ({ theme }: { theme: any }) => css`
        background: ${theme.button.footer.background};
        border: 1px solid ${theme.button.footer.border};
        color: ${theme.button.footer.text};
        svg {
            color: ${theme.button.footer.icon};
        }
        &:hover {
            background: ${theme.button.footer.hoverBackground};
            color: ${theme.button.footer.hoverText};
        }
    `,
    send: ({ theme }: { theme: any }) => css`
        background: ${theme.button.send.background};
        border: 1px solid ${theme.button.send.border};
        color: ${theme.button.send.text};
        svg {
            color: ${theme.button.send.icon};
        }
        &:hover {
            background: ${theme.button.send.hoverBackground};
            color: ${theme.button.send.hoverText};
        }
    `,
    danger: ({ theme }: { theme: any }) => css`
        background: ${theme.button.danger.background};
        border: 1px solid ${theme.button.danger.border};
        color: ${theme.button.danger.text};
        svg {
            color: ${theme.button.danger.icon};
        }
        &:hover {
            background: ${theme.button.danger.hoverBackground};
            color: ${theme.button.danger.hoverText};
            svg {
                color: ${theme.button.danger.hoverText};
            }
        }
    `,
    // ─────────────────────────────────────────────
    // TEXT / LINK-STYLE VARIANTS
    // Folded in from the former TextButton component.
    // These reference color/banner tokens directly and
    // skip the filled-button base + size styles (see
    // sharedStyles → TEXT_VARIANTS branch below).
    // ─────────────────────────────────────────────
    text: ({ theme }: { theme: any }) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        ${TextSize90}
        font-weight: 500;
        color: ${theme.color.textBody};
        &:hover {
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `,
    context: ({ theme }: { theme: any }) => css`
        ${TextSize100}
        font-weight: 400;
        color: ${theme.color.link};
        text-decoration: underline !important;
        &:hover {
            color: ${theme.color.text};
            text-decoration: none;
        }
    `,
    control: ({ theme }: { theme: any }) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        ${TextSize100}
        font-weight: 500;
        color: ${theme.color.textBody};
        &:hover {
            cursor: pointer;
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `,
    negative: ({ theme }: { theme: any }) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        ${TextSize90}
        font-weight: 500;
        color: ${theme.banner.error.linkText};
        &:hover {
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `,
    subsection: ({ theme }: { theme: any }) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        ${TextSize100}
        font-weight: 400;
        color: ${theme.color.link};
        text-decoration: underline !important;
        &:hover {
            cursor: pointer;
            color: ${theme.color.link};
            text-decoration: underline;
        }
    `,
} as const;

export type ButtonVariant = keyof typeof variantStyles;

// Text/link-style variants use a transparent, borderless base and opt out of
// the filled-button base + touch-target size styles.
const TEXT_VARIANTS = new Set<ButtonVariant>(['text', 'context', 'control', 'negative', 'subsection']);

const textVariantBaseStyles = css`
    align-items: center;
    background-color: transparent;
    border: none;
    cursor: pointer;
    font-family: 'Archivo', sans-serif;
    padding: 0.25rem 1rem 0.25rem 0;
    text-decoration: none;
    ${MixinFocusVisible}
    @media (prefers-reduced-motion: no-preference) {
        transition: all 0.1s ease-out;
    }
    svg {
        vertical-align: middle;
        scale: 0.8;
    }
`;

// ─────────────────────────────────────────────
// STYLED PRIMITIVE
// Single styled element — variant and size are
// applied as composed styles, not separate
// components per variant.
// ─────────────────────────────────────────────

type ButtonElement = HTMLButtonElement | HTMLAnchorElement;

type SharedStyleProps = {
    $color: ButtonVariant;
    $size: ButtonSize;
    $dashed?: boolean;
    $pill?: boolean;
    disabled?: boolean;
};

// Shared style function — single source of truth for all three primitives.
// Filled variants get buttonBaseStyles + size styles; text/link variants get
// the transparent text base and skip sizing entirely.
const sharedStyles = ({ theme, $color, $size, $dashed, $pill, disabled }: SharedStyleProps & { theme: any }) => {
    const isText = TEXT_VARIANTS.has($color);
    return css`
        ${isText ? textVariantBaseStyles : buttonBaseStyles}
        ${variantStyles[$color]?.({ theme })}
        ${!isText && (theme.scaleSize > 16 ? aaaSizeStyles[$size] : sizeStyles[$size])}
        ${$dashed && 'border-style: dashed;'}
        ${$pill && `border-radius: ${borderRadius.pill};`}
        ${disabled && (isText ? disabledTextButtonStyles({ theme }) : disabledButtonStyles({ theme }))}
    `;
};

const shouldForwardProp = (prop: string) => !['$color', '$size', '$dashed', '$pill'].includes(prop);

// Three typed primitives sharing one style function.
// button → <button>, href → <a>, to → <NavLink>
const StyledButton = styled('button', { shouldForwardProp })<SharedStyleProps>(sharedStyles);

const StyledAnchor = styled('a', { shouldForwardProp })<SharedStyleProps>(sharedStyles);

const StyledNavLink = styled(NavLink, { shouldForwardProp })<SharedStyleProps>(sharedStyles);

// ─────────────────────────────────────────────
// CORE BUTTON
// Renders as button, <a>, or NavLink depending
// on props. All three share the same styled
// primitive via the `as` prop — no duplicate
// styled components needed.
// ─────────────────────────────────────────────

const Button = forwardRef<ButtonElement, ICoreButtonProps>(
    (
        {
            type = 'button',
            $color = 'primary',
            $size = 'medium',
            disabled = false,
            onClick,
            href,
            to,
            target,
            iconSize,
            startIcon,
            endIcon,
            startComponent,
            endComponent,
            children,
            ...restProps
        },
        ref
    ) => {
        // Auto-apply external link icon for blank targets
        if (target === '_blank' && href?.startsWith('http')) {
            endIcon = endIcon ?? 'externalLink';
        }
        // Default anchor target
        if (href && !target) target = '_self';

        // Text/link variants render smaller icons (matching the former
        // TextButton); filled variants default to 1.5rem.
        const isTextVariant = TEXT_VARIANTS.has($color);
        const resolvedIconSize = iconSize ?? (isTextVariant ? 1 : 1.5);

        const sharedProps = {
            $color,
            $size,
            disabled,
            target,
            className: 'core-button',
            ...restProps,
        };

        const content = (
            <>
                {startComponent}
                {startIcon && <Icons name={startIcon} size={resolvedIconSize} />}
                {children}
                {endComponent}
                {endIcon && <Icons name={endIcon} size={resolvedIconSize} />}
            </>
        );

        if (href) {
            return (
                <StyledAnchor
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className="core-button"
                    href={href}
                    target={target}
                    $color={$color}
                    $size={$size}
                    {...restProps}>
                    {content}
                </StyledAnchor>
            );
        }

        if (to) {
            return (
                <StyledNavLink
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className="core-button"
                    viewTransition
                    to={to}
                    $color={$color}
                    $size={$size}
                    {...restProps}>
                    {content}
                </StyledNavLink>
            );
        }

        return (
            <StyledButton
                ref={ref as React.Ref<HTMLButtonElement>}
                className="core-button"
                type={type}
                onClick={onClick}
                disabled={disabled}
                $color={$color}
                $size={$size}
                {...restProps}>
                {content}
            </StyledButton>
        );
    }
);

Button.displayName = 'CoreButton';

export default Button;
